import { inject, Injectable } from "@angular/core";
import { Device } from "@domain/product/monitoring/device";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductService } from "@domain/product/product.service";
import { forkJoin, map, Observable, of } from "rxjs";
import {
  MonitoringGroupedPartRow,
  MonitoringGroupedPartSection,
  MonitoringGroupedPartSourceItem,
} from "./monitoring-grouped-parts.types";

@Injectable({ providedIn: "root" })
export class MonitoringGroupedPartsService {
  private monitoringProductService = inject(MonitoringProductService);
  private productService = inject(ProductService);

  buildSections(
    items: MonitoringGroupedPartSourceItem[],
    category: MonitoringCategory,
    otherProductsLabel: string,
    hideUnavailable = false,
  ): Observable<MonitoringGroupedPartSection[]> {
    if (!items?.length) return of([]);

    const rowStreams = items.map((item) => this.buildRowsForItem(item, category, hideUnavailable));

    return forkJoin(rowStreams).pipe(
      map((rowsByItem) => {
        return this.buildSectionsFromRows(rowsByItem, otherProductsLabel);
      }),
    );
  }

  private buildSectionsFromRows(
    rowsByItem: { title: string; rows: MonitoringGroupedPartRow[]; isGroup: boolean }[],
    otherProductsLabel: string,
  ): MonitoringGroupedPartSection[] {
    const sections: MonitoringGroupedPartSection[] = [];
    const otherRows: MonitoringGroupedPartRow[] = [];
    const sectionIndex = new Map<string, number>();

    rowsByItem.forEach((result) => {
      if (!result.rows.length) return;

      if (result.isGroup) {
        const title = result.title;
        const existingIndex = sectionIndex.get(title);

        if (existingIndex !== undefined) {
          sections[existingIndex].rows.push(...result.rows);
        } else {
          sectionIndex.set(title, sections.length);
          sections.push({ title, rows: [...result.rows] });
        }
      } else {
        otherRows.push(...result.rows);
      }
    });

    if (otherRows.length) {
      sections.push({ title: otherProductsLabel, rows: otherRows });
    }

    return sections;
  }

  private buildRowsForItem(item: MonitoringGroupedPartSourceItem, category: MonitoringCategory, hideUnavailable: boolean) {
    const option = item.option;
    const deviceIds = (option?.devices ?? []).map((device) => String(device));

    if (!deviceIds.length) {
      return of({ title: item.category, rows: [] as MonitoringGroupedPartRow[], isGroup: true });
    }

    const filterCountry = this.productService.getFilterCountry();

    return this.monitoringProductService.loadDevicesFromDB(deviceIds, category, filterCountry).pipe(
      map((devices) => {
        const deviceMap = new Map<string, Device>();
        devices.forEach((device) => deviceMap.set(String(device.matNr), device));
        const rows = deviceIds
          .map((deviceId) => this.buildRow(item, deviceId, deviceMap.get(deviceId)))
          .filter((row) => !hideUnavailable || row.isAvailable !== false);

        return { title: item.category, rows, isGroup: true };
      }),
    );
  }

  private buildRow(item: MonitoringGroupedPartSourceItem, deviceId: string, device?: Device): MonitoringGroupedPartRow {
    const option = item.option;
    const name = device?.name || option?.groupName || deviceId;
    const description = device?.description || option?.description || "";
    const configurationURL = device?.configurationURL || option?.configurationURL || "";
    const piSheetURL = device?.piSheetURL || option?.piSheetURL || "";

    return {
      quantity: item.quantity,
      name,
      partNumber: deviceId,
      isAvailable: device?.isAvailable,
      category: item.category,
      description,
      configurationURL,
      piSheetURL,
    };
  }
}
